"use client";

import {
  LandingSiteTemplateGetDataPackagesItemDataSourceOutputApiModel,
  LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel,
} from "@/Apis/ClinicApi";
import { Format, Html } from "@/Components";
import { Formats } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { useT } from "@/Hooks";
import { PackagesSectionProps } from "@/Templates/SectionsProps";
import { FC, useMemo } from "react";
import HomeTwoSpecialitySectionTwoGenerated, {
  HomeTwoSpecialitySectionTwoGeneratedProps,
} from "../Sections/_generated/HomeTwoSpecialitySectionTwo.generated";
import PricingSectionGenerated, {
  PricingSectionGeneratedProps,
} from "../Sections/_generated/PricingSection.generated";
import {
  buildGeneratedSectionProps,
  GeneratedSectionRenderer,
  renderGeneratedSection,
} from "./generated-section-renderer";

type PackagesAppearanceKey = ChildrenOf<"SiteTemplatePackagesAppearance">;

const defaultPackagesAppearanceKey: PackagesAppearanceKey =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplatePackagesAppearance
    .Default.key;

function buildCenterItemMap(
  items: LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel[],
) {
  return new Map(
    items.map(
      (item) =>
        [item.Id, item] satisfies [
          number,
          LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel,
        ],
    ),
  );
}

function getPackageCenterItems(
  item: LandingSiteTemplateGetDataPackagesItemDataSourceOutputApiModel,
  centerItemMap: Map<
    number,
    LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel
  >,
) {
  return (item.Items ?? [])
    .map((centerItemId) => centerItemMap.get(centerItemId))
    .filter(
      (
        centerItem,
      ): centerItem is LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel =>
        Boolean(centerItem),
    );
}

function getPackageNetPrice(
  item: LandingSiteTemplateGetDataPackagesItemDataSourceOutputApiModel,
  centerItemMap: Map<
    number,
    LandingSiteTemplateGetDataPackagesItemItemDataSourceOutputApiModel
  >,
) {
  const totalPrice = getPackageCenterItems(item, centerItemMap).reduce(
    (sum, centerItem) => sum + centerItem.SalePrice,
    0,
  );

  return Math.max(totalPrice - item.DiscountAmount, 0);
}

function getPackageTitle(
  item: LandingSiteTemplateGetDataPackagesItemDataSourceOutputApiModel,
) {
  return item.Name || item.ProductName;
}

function getPackageSubtitle(
  item: LandingSiteTemplateGetDataPackagesItemDataSourceOutputApiModel,
) {
  return (
    (item.DiscountAmount
      ? `${Formats.Number(item.DiscountAmount)} تخفیف`
      : undefined) ||
    item.Description ||
    item.ProductName
  );
}

function getPackageTrialText(
  t: ReturnType<typeof useT>,
  trialDaysCount: number,
) {
  return t(
    "landing.package.list.daysCount",
    { value0: trialDaysCount },
    `${trialDaysCount} days`,
  );
}

const DefaultPackagesRenderer: GeneratedSectionRenderer<
  PackagesSectionProps
> = (props) => {
  const t = useT();

  const generatedProps = useMemo<PricingSectionGeneratedProps>(() => {
    const centerItemMap = buildCenterItemMap(
      props.dataSource.CenterItems ?? [],
    );

    return buildGeneratedSectionProps<PricingSectionGeneratedProps>(
      "PricingSection",
      {
        sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title,
        sectionDiv0Div1ClassName: "row justify-content-center",
        pricing46s: (props.dataSource.Items ?? []).map((item, index) => ({
          key: item.Id,
          className:
            index === 1
              ? "card pricing-card active w-100"
              : "card pricing-card w-100",
          src: [
            "/Templates/Doccure/img/icons/price-icon1.svg",
            "/Templates/Doccure/img/icons/price-icon2.svg",
            "/Templates/Doccure/img/icons/price-icon3.svg",
          ][index % 3],
          h40Text: getPackageTitle(item),
          p0Text: getPackageSubtitle(item),
          h20Text: (
            <Format
              type="Number"
              value={getPackageNetPrice(item, centerItemMap)}
            />
          ),
          span0Span0Text: t("landing.package.list.yearly", undefined, "Yearly"),
          h60Text: getPackageTrialText(t, item.TrialDaysCount),
          href: "/Packages",
          link0Text: t(
            "landing.package.list.startTrialPeriod",
            undefined,
            "Start trial",
          ),
          pricingListItems: getPackageCenterItems(item, centerItemMap).map(
            (centerItem) => ({
              key: `${item.Id}-${centerItem.Id}`,
              liText: centerItem.Name,
            }),
          ),
        })),
      },
    );
  }, [
    props.dataSource.CenterItems,
    props.dataSource.Items,
    props.setting.Title,
    t,
  ]);

  return <PricingSectionGenerated {...generatedProps} />;
};

const Home2PackagesRenderer: GeneratedSectionRenderer<PackagesSectionProps> = (
  props,
) => {
  const t = useT();

  const generatedProps =
    useMemo<HomeTwoSpecialitySectionTwoGeneratedProps>(() => {
      const centerItemMap = buildCenterItemMap(
        props.dataSource.CenterItems ?? [],
      );
      const packageItems = props.dataSource.Items ?? [];
      const visibleCollectionItems = packageItems.map((item) => ({
        key: item.Id,
        div0Text: (
          <Format
            type="Number"
            value={getPackageNetPrice(item, centerItemMap)}
          />
        ),
        p0Text: getPackageTitle(item),
        span0Text: t("landing.package.list.yearly", undefined, "Yearly"),
        link0Text: t(
          "landing.package.list.startTrialPeriod",
          undefined,
          "Start trial",
        ),
        href: "/Packages",
        h30Text: getPackageTrialText(t, item.TrialDaysCount),
        priceBodyItems: getPackageCenterItems(item, centerItemMap).map(
          (centerItem) => ({
            key: `${item.Id}-${centerItem.Id}`,
            liText: centerItem.Name,
          }),
        ),
      }));

      return buildGeneratedSectionProps<HomeTwoSpecialitySectionTwoGeneratedProps>(
        "HomeTwoSpecialitySectionTwo",
        {
          sectionDiv0Div0Div0DivText: props.setting.Description,
          sectionDiv0Div0H20H2Text: (
            <Html value={props.setting.Title} as="span" />
          ),
          sectionDiv0Div0H20Span0SpanText: "",
          sectionDiv0Div1ClassName: "row align-items-start g-4",
          specialitySectionTwo36s: visibleCollectionItems,
        },
      );
    }, [
      props.dataSource.CenterItems,
      props.dataSource.Items,
      props.setting.Description,
      props.setting.Title,
      t,
    ]);

  return <HomeTwoSpecialitySectionTwoGenerated {...generatedProps} />;
};

const generatedPackagesRenderers = {
  [defaultPackagesAppearanceKey]: DefaultPackagesRenderer,
  [CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplatePackagesAppearance.Home2.key]: Home2PackagesRenderer,
} satisfies Partial<
  Record<PackagesAppearanceKey, GeneratedSectionRenderer<PackagesSectionProps>>
>;

const GeneratedPackagesSection: FC<PackagesSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedPackagesRenderers,
    defaultPackagesAppearanceKey,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedPackagesSection;
